import { type FocusEvent, type ReactElement, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  datingFromGestationalAge,
  formatDatingDate,
  gestationalAgeDays,
  gestationalAgeOf,
  parseDatingDate,
  POST_TERM_WARNING_DAYS,
  type PregnancyDating,
} from '@gene42/pedigree/pregnancy-dating';

import { DateInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';

/**
 * Gestational-age entry for the pregnancy-dating control. A GA is only true on the day it was
 * measured, so it is entered together with an as-of date and converted on entry to an LMP anchor
 * (basis GA); nothing here stores a drifting number.
 *
 * While the clinician is typing, the field holds their draft. Once focus leaves the group with a valid
 * entry, it goes back to showing the GA derived for today, which differs from what was typed whenever
 * the as-of date is in the past.
 */
export interface GestationalAgeInputProps {
  readonly idPrefix: string;
  readonly dating: PregnancyDating | null;
  readonly disabled?: boolean | undefined;
  readonly onChange: (next: PregnancyDating | null) => void;
}

interface GaDraft {
  readonly weeks: string;
  readonly days: string;
  readonly asOf: string;
}

type DraftCheck =
  | { readonly kind: 'clear' }
  | { readonly kind: 'invalid'; readonly message: string }
  | { readonly kind: 'valid'; readonly dating: PregnancyDating; readonly postTerm: boolean };

function todayIso(): string {
  const now = new Date();
  return formatDatingDate({ year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() }) ?? '';
}

const WHOLE_NUMBER = /^\d+$/;

/** Value identity for dating: callers rebuild the object every render, so compare by content. */
function datingKey(dating: PregnancyDating | null): string {
  if (dating === null) return '';
  return [formatDatingDate(dating.anchor), dating.basis, formatDatingDate(dating.asOf ?? null)].join('|');
}

export function GestationalAgeInput({
  idPrefix,
  dating,
  disabled,
  onChange,
}: GestationalAgeInputProps): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<GaDraft | null>(null);
  const settled = useRef('');
  // The dating as it stood when this edit began; an invalid entry falls back to it rather than
  // keeping whatever valid value an earlier keystroke produced (typing "45" passes through "4").
  const baseline = useRef<PregnancyDating | null>(null);

  // A value that arrives from outside (another field, a reset, a reload) supersedes the draft.
  if (draft !== null && datingKey(dating) !== settled.current) {
    setDraft(null);
  }

  const derived = gestationalAgeOf(dating);
  const shown: GaDraft = draft ?? {
    weeks: derived === null ? '' : String(derived.weeks),
    days: derived === null ? '' : String(derived.days),
    asOf: todayIso(),
  };

  const check = (d: GaDraft): DraftCheck => {
    if (d.weeks === '' && d.days === '') return { kind: 'clear' };
    const asOf = parseDatingDate(d.asOf);
    if (asOf === null) {
      return { kind: 'invalid', message: t('patient.demographics.gaAsOfRequired', { defaultValue: 'Enter the date the gestational age was measured.' }) };
    }
    if (d.asOf > todayIso()) {
      return { kind: 'invalid', message: t('patient.demographics.gaAsOfFuture', { defaultValue: "The as-of date can't be in the future." }) };
    }
    const ga = WHOLE_NUMBER.test(d.weeks) && (d.days === '' || WHOLE_NUMBER.test(d.days))
      ? { weeks: Number(d.weeks), days: d.days === '' ? 0 : Number(d.days) }
      : null;
    const next = ga === null ? null : datingFromGestationalAge(ga, asOf);
    if (ga === null || next === null) {
      return { kind: 'invalid', message: t('patient.demographics.gaRange', { defaultValue: 'Enter a gestational age from 0w 0d to 44w 6d.' }) };
    }
    return { kind: 'valid', dating: next, postTerm: (gestationalAgeDays(ga) ?? 0) > POST_TERM_WARNING_DAYS };
  };

  const update = (patch: Partial<GaDraft>) => {
    if (draft === null) baseline.current = dating;
    const next = { ...shown, ...patch };
    setDraft(next);
    const result = check(next);
    const value = result.kind === 'valid' ? result.dating : result.kind === 'clear' ? null : baseline.current;
    settled.current = datingKey(value);
    if (datingKey(value) !== datingKey(dating)) onChange(value);
  };

  const status = draft === null ? null : check(draft);

  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    // An invalid draft stays on screen with its error rather than silently reverting.
    if (status?.kind !== 'invalid') setDraft(null);
  };

  return (
    <div className="flex flex-col gap-1" onBlur={handleBlur}>
      <span className="text-xs font-medium text-muted-foreground">
        {t('patient.demographics.gestationalAge', { defaultValue: 'Gestational age' })}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <NumberPart
          id={`${idPrefix}-ga-weeks`}
          label={t('patient.demographics.gaWeeks', { defaultValue: 'Gestational age weeks' })}
          suffix="w"
          value={shown.weeks}
          disabled={disabled}
          onChange={(weeks) => { update({ weeks }); }}
        />
        <NumberPart
          id={`${idPrefix}-ga-days`}
          label={t('patient.demographics.gaDays', { defaultValue: 'Gestational age days' })}
          suffix="d"
          value={shown.days}
          disabled={disabled}
          onChange={(days) => { update({ days }); }}
        />
      </div>
      {draft !== null && (
        <div className="flex items-center gap-2">
          <label htmlFor={`${idPrefix}-ga-as-of`} className="whitespace-nowrap text-xs text-muted-foreground">
            {t('patient.demographics.gaAsOf', { defaultValue: 'as of' })}
          </label>
          <DateInput
            id={`${idPrefix}-ga-as-of`}
            value={draft.asOf}
            disabled={disabled ?? false}
            onChange={(asOf) => { update({ asOf }); }}
            className="flex-1"
          />
        </div>
      )}
      {status?.kind === 'invalid' && (
        <p role="alert" className="text-xs text-destructive">{status.message}</p>
      )}
      {status?.kind === 'valid' && status.postTerm && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          {t('patient.demographics.gaPostTerm', { defaultValue: 'Over 42 weeks. Check this gestational age.' })}
        </p>
      )}
    </div>
  );
}

interface NumberPartProps {
  readonly id: string;
  readonly label: string;
  readonly suffix: string;
  readonly value: string;
  readonly disabled?: boolean | undefined;
  readonly onChange: (value: string) => void;
}

function NumberPart({ id, label, suffix, value, disabled, onChange }: NumberPartProps): ReactElement {
  return (
    <span className="flex items-center gap-1">
      <Input
        id={id}
        aria-label={label}
        type="text"
        inputMode="numeric"
        value={value}
        disabled={disabled}
        onChange={(e) => { onChange(e.target.value.trim()); }}
        className="h-8 w-14"
      />
      <span className="text-xs text-muted-foreground">{suffix}</span>
    </span>
  );
}
